import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { ShareButtons } from "./ShareButtons";

export const VideoCard = ({ src, poster, label, accent, testId }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };

  return (
    <div
      className="relative aspect-[9/16] w-full overflow-hidden rounded-3xl bg-[#132338] ring-1 ring-white/10 shadow-2xl group"
      data-testid={testId}
    >
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onClick={toggle}
        className="h-full w-full object-cover cursor-pointer"
      />
      <span
        className={`absolute top-4 left-4 rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-white ${
          accent ? "bg-[#F26522]" : "bg-[#0E1A2B]/85 backdrop-blur-md"
        } ${label ? "" : "hidden"}`}
      >
        {label}
      </span>
      <div className="absolute bottom-4 right-4 flex gap-2">
        <button
          onClick={() => setMuted((m) => !m)}
          aria-label={muted ? "Unmute" : "Mute"}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0E1A2B]/80 backdrop-blur-md text-white hover:bg-[#F26522] transition-colors duration-200"
          data-testid={`${testId}-mute-btn`}
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
        <button
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0E1A2B]/80 backdrop-blur-md text-white hover:bg-[#F26522] transition-colors duration-200"
          data-testid={`${testId}-play-btn`}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>
      </div>
    </div>
  );
};

export const VideoBeforeAfter = () => {
  const { t } = useLang();
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7 }}
      className="mt-16 rounded-[2rem] bg-[#0E1A2B] grain relative overflow-hidden p-6 sm:p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,560px)] gap-10 lg:gap-16 items-center"
      data-testid="gallery-video-section"
    >
      <div className="relative z-10 max-w-md">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F26522] font-semibold mb-4">
          {t("vid_eyebrow")}
        </p>
        <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
          {t("vid_h")}
        </h3>
        <p className="mt-4 text-base leading-relaxed text-slate-300">{t("vid_sub")}</p>
        <ul className="mt-6 space-y-3">
          {["vid_step1", "vid_step2", "vid_step3"].map((k, i) => (
            <li key={k} className="flex items-start gap-3 text-sm text-slate-200">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F26522]/15 font-mono text-[11px] font-bold text-[#F26522]">
                {i + 1}
              </span>
              {t(k)}
            </li>
          ))}
        </ul>
        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          {t("vid_hint")}
        </p>
        <div className="mt-6">
          <ShareButtons dark />
        </div>
      </div>
      <div className="relative z-10 grid grid-cols-2 gap-4 sm:gap-6 lg:translate-y-0">
        <VideoCard
          src="/before-video.mp4"
          poster="/before-video-poster.jpg"
          label={t("gal_before")}
          testId="video-before"
        />
        <div className="sm:mt-10">
          <VideoCard
            src="/after-video.mp4"
            poster="/after-video-poster.jpg"
            label={t("gal_after")}
            accent
            testId="video-after"
          />
        </div>
      </div>
    </motion.div>
  );
};
